import React, { useEffect, useContext } from 'react';  
import { useNavigate, useParams } from "react-router-dom";
import Axios from "axios";
import { useImmerReducer } from 'use-immer';
import {
    Grid, Snackbar, Typography, Button, TextField, MenuItem,
    Alert, Container, Paper, Box, CircularProgress
} from "@mui/material";
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import { useTranslation } from 'react-i18next';
import StateContext from "../../../Contexts/StateContext";

import {
    areaOptions, AranyosszekOptions, CsikszekOptions, GyergyoszekOptions, KaszonfiuszekOptions, KereszturfiuszekOptions,
    KezdiszekOptions, MarosszekOptions, MiklosvarfiuszekOptions, OrbaiszekOptions, SepsiszekOptions, UdvarhelyszekOptions,
} from "../../../Constants/GeographicConstants";

const defaultIcon = new L.Icon({
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
});

function AddSight() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { sightType } = useParams();
    const GlobalState = useContext(StateContext);

    const config = require('../../../config').default;
    const environment = require('../../../config').environment;

    const initialState = {
        titleValue: '',
        descriptionValue: '',
        areaValue: '',
        boroughValue: '',
        latitudeValue: 45.8643,
        longitudeValue: 25.7848,
        uploadedPictures: [],
        addressValue: '',
        geocodingError: '',
        geocodingSuccess: false,
        sendRequest: 0,
        openSnack: false,
        disabledBtn: false,
        error: '',
        // form errors
        titleErrors: { hasError: false, errorMessage: '' },
        areaErrors: { hasError: false, errorMessage: '' },
        boroughErrors: { hasError: false, errorMessage: '' },
    };

    function ourReducer(draft, action) {
        switch (action.type) {
            case 'catchTitleChange':
                draft.titleValue = action.value;
                draft.titleErrors.hasError = false;
                draft.titleErrors.errorMessage = '';
                break;
            case 'catchDescriptionChange':
                draft.descriptionValue = action.value;
                break;
            case 'catchAreaChange':
                draft.areaValue = action.value;
                draft.areaErrors.hasError = false;
                draft.areaErrors.errorMessage = '';
                break;
            case 'catchBoroughChange':
                draft.boroughValue = action.value;
                draft.boroughErrors.hasError = false;
                draft.boroughErrors.errorMessage = '';
                break;
            case 'catchLatitudeChange':
                draft.latitudeValue = action.value;
                break;
            case 'catchLongitudeChange':
                draft.longitudeValue = action.value;
                break;
            case 'catchAddressChange':
                draft.addressValue = action.value;
                draft.geocodingError = '';
                draft.geocodingSuccess = false;
                break;
            case 'setGeocodingError':
                draft.geocodingError = action.value;
                draft.geocodingSuccess = false;
                break;
            case 'setGeocodingSuccess':
                draft.geocodingSuccess = true;
                draft.geocodingError = '';
                break;
            case 'catchUploadedPictures':
                draft.uploadedPictures = action.value;
                break;
            case 'changeSendRequest':
                draft.sendRequest++;
                break;
            case 'openTheSnack':
                draft.openSnack = true;
                break;
            case 'disableTheBtn':
                draft.disabledBtn = true;
                break;
            case 'enableTheBtn':
                draft.disabledBtn = false;
                break;
            case 'catchTitleErrors':
                draft.titleErrors.hasError = true;
                draft.titleErrors.errorMessage = action.value;
                break;
            case 'catchAreaErrors':
                draft.areaErrors.hasError = true;
                draft.areaErrors.errorMessage = action.value;
                break;
            case 'catchBoroughErrors':
                draft.boroughErrors.hasError = true;
                draft.boroughErrors.errorMessage = action.value;
                break;
            case 'setError':
                draft.error = action.value;
                break;
            default:
                break;
        }
    }

    const [state, dispatch] = useImmerReducer(ourReducer, initialState);

    function TheMapComponent() {
        const map = useMap();
        useEffect(() => {
            map.on('click', function (e) {
                dispatch({ type: 'catchLatitudeChange', value: e.latlng.lat });
                dispatch({ type: 'catchLongitudeChange', value: e.latlng.lng });
            });
        }, [map]);
        return null;
    }

    const getBoroughOptions = () => {
        switch (state.areaValue) {
            case 'Aranyosszék': return AranyosszekOptions;
            case 'Csíkszék': return CsikszekOptions;
            case 'Gyergyószék': return GyergyoszekOptions;
            case 'Kászonszék': return KaszonfiuszekOptions;
            case 'Keresztúrszék': return KereszturfiuszekOptions;
            case 'Kézdiszék': return KezdiszekOptions;
            case 'Marosszék': return MarosszekOptions;
            case 'Miklósvárszék': return MiklosvarfiuszekOptions;
            case 'Orbaiszék': return OrbaiszekOptions;
            case 'Sepsiszék': return SepsiszekOptions;
            case 'Udvarhelyszék': return UdvarhelyszekOptions;
            default: return [];
        }
    };

    const getEndpoint = () => {
        switch (sightType) {
            case 'built-heritage': return config[environment].builtHeritageEndpoint;
            case 'mineral-water': return config[environment].mineralWaterEndpoint;
            case 'gyalogtura': return config[environment].sportEndpoint;
            case 'lovastura': return config[environment].sportEndpoint;
            // Add other types as needed, or use a generic one
            default: return config[environment].geographicEndpoint;
        }
    };

    const getTitle = () => {
        switch (sightType) {
            case 'built-heritage': return t('SubMenu.BuiltHeritage');
            case 'rovasemlek': return t('SubMenu.RunicScripts');
            case 'neprajzi': return t('SubMenu.Ethnographic');
            case 'egyhazi': return t('SubMenu.Spiritual');
            case 'geological': return t('SubMenu.Geological');
            case 'novenyvilag': return t('SubMenu.Botanical');
            case 'mineral-water': return t('SubMenu.MineralWater');
            case 'gyalogtura': return t('SubMenu.Tracking');
            case 'lovastura': return t('SubMenu.Equestrian');
            default: return t('SubMenu.MapObject');
        }
    };

    const handleGeocode = async () => {
        if (!state.addressValue) return;
        try {
            const response = await Axios.get(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(state.addressValue)}`);
            if (response.data && response.data.length > 0) {
                const { lat, lon } = response.data[0];
                dispatch({ type: 'catchLatitudeChange', value: parseFloat(lat) });
                dispatch({ type: 'catchLongitudeChange', value: parseFloat(lon) });
                dispatch({ type: 'setGeocodingSuccess' });
            } else {
                dispatch({ type: 'setGeocodingError', value: t('Thematic.GeocodingNoResult', 'Nem található koordináta a megadott címhez.') });
            }
        } catch (e) {
            console.error("Geocoding error:", e);
            dispatch({ type: 'setGeocodingError', value: t('Thematic.GeocodingError', 'Hiba történt a keresés során.') });
        }
    };

    function FormSubmit(e) {
        e.preventDefault();

        if (!state.titleValue) {
            dispatch({ type: 'catchTitleErrors', value: t('Validation.EmptyTitle') });
            window.scrollTo(0, 0);
            return;
        }
        if (!state.areaValue) {
            dispatch({ type: 'catchAreaErrors', value: t('Validation.EmptyArea', 'Kérjük válasszon széket!') });
            window.scrollTo(0, 0);
            return;
        }
        if (!state.boroughValue) {
            dispatch({ type: 'catchBoroughErrors', value: t('Validation.EmptyBorough', 'Kérjük válasszon települést!') });
            window.scrollTo(0, 0);
            return;
        }

        dispatch({ type: 'changeSendRequest' });
        dispatch({ type: 'disableTheBtn' });
    }

    useEffect(() => {
        if (state.sendRequest) {
            async function AddSightFunc() {
                const formData = new FormData();
                formData.append('title', state.titleValue);
                formData.append('description', state.descriptionValue);
                formData.append('address', state.addressValue);
                formData.append('area', state.areaValue);
                formData.append('borough', state.boroughValue);
                formData.append('latitude', state.latitudeValue);
                formData.append('longitude', state.longitudeValue);
                formData.append('type', sightType);
                formData.append('owner', GlobalState.userId);

                // Add pictures
                for (let i = 0; i < state.uploadedPictures.length; i++) {
                    formData.append(`picture${i + 1}`, state.uploadedPictures[i]);
                }

                try {
                    await Axios.post(getEndpoint(), formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                        }
                    });
                    dispatch({ type: 'openTheSnack' });
                } catch (e) {
                    console.error(e.response);
                    dispatch({ type: 'enableTheBtn' });
                    dispatch({ type: 'setError', value: t('Common.Error', 'Hiba történt a mentés során.') });
                }
            }
            AddSightFunc();
        }
    }, [state.sendRequest]);

    useEffect(() => {
        if (state.openSnack) {
            setTimeout(() => {
                navigate(-1);
            }, 1500);
        }
    }, [state.openSnack]);

    if (!GlobalState.userIsLogged) {
        return (
            <Container maxWidth="sm" sx={{ py: 8 }}>
                <Alert severity="warning">
                    {t('Administration.LoginRequired')}
                </Alert>
                <Button variant="contained" onClick={() => navigate('/login')} sx={{ mt: 2 }}>
                    {t('Administration.LoginButton')}
                </Button>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ py: 4, mt: '50px' }}>
            <Paper sx={{ p: 4 }}>
                <Typography variant="h4" gutterBottom sx={{ color: 'var(--sznodarkblue)', fontWeight: 'bold' }}>
                    {getTitle()} +
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                    {t('MapObject.RegisterIntro')}
                </Typography>

                <form onSubmit={FormSubmit}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label={t('Common.Title')}
                                value={state.titleValue}
                                onChange={(e) => dispatch({ type: 'catchTitleChange', value: e.target.value })}
                                error={state.titleErrors.hasError}
                                helperText={state.titleErrors.errorMessage}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                fullWidth
                                label={t('Map.Toolbox.szek')}
                                value={state.areaValue}
                                onChange={(e) => dispatch({ type: 'catchAreaChange', value: e.target.value })}
                                error={state.areaErrors.hasError}
                                helperText={state.areaErrors.errorMessage}
                                required
                            >
                                {areaOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                fullWidth
                                label={t('Map.Toolbox.telepules')}
                                value={state.boroughValue}
                                onChange={(e) => dispatch({ type: 'catchBoroughChange', value: e.target.value })}
                                error={state.boroughErrors.hasError}
                                helperText={state.boroughErrors.errorMessage}
                                required
                                disabled={!state.areaValue}
                            >
                                {getBoroughOptions().map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label={t('Common.Description')}
                                value={state.descriptionValue}
                                onChange={(e) => dispatch({ type: 'catchDescriptionChange', value: e.target.value })}
                            />
                        </Grid>

                        <Grid item xs={12} sm={9}>
                            <TextField
                                fullWidth
                                label={t('Common.Address')}
                                value={state.addressValue}
                                onChange={(e) => dispatch({ type: 'catchAddressChange', value: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={handleGeocode}
                                sx={{ height: '56px' }}
                            >
                                {t('Thematic.Geocode')}
                            </Button>
                        </Grid>
                        {state.geocodingError && (
                            <Grid item xs={12}>
                                <Alert severity="error" sx={{ mt: 1 }}>
                                    {state.geocodingError}
                                </Alert>
                            </Grid>
                        )}
                        {state.geocodingSuccess && (
                            <Grid item xs={12}>
                                <Alert severity="success" sx={{ mt: 1 }}>
                                    {t('Thematic.GeocodingSuccess')}
                                </Alert>
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>
                                {t('Common.Location')}
                            </Typography>
                            <Box sx={{ height: '400px', width: '100%' }}>
                                <MapContainer center={[state.latitudeValue, state.longitudeValue]} zoom={13} scrollWheelZoom={true} style={{ height: '100%', borderRadius: '8px' }}>
                                    <TileLayer url={config[environment].osmTiles} />
                                    <TheMapComponent />
                                    <Marker position={[state.latitudeValue, state.longitudeValue]} icon={defaultIcon} />
                                </MapContainer>
                            </Box>
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                                <Grid item xs={6}>
                                    <TextField fullWidth label="Latitude" value={state.latitudeValue} disabled />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField fullWidth label="Longitude" value={state.longitudeValue} disabled />
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>
                                {t('Common.Pictures', 'Képek feltöltése')}
                            </Typography>
                            <Button
                                variant="contained"
                                component="label"
                                sx={{ bgcolor: 'var(--sznodarkblue)' }}
                            >
                                {t('Common.UploadPictures', 'Képek feltöltése')}
                                <input
                                    type="file"
                                    multiple
                                    hidden
                                    onChange={(e) => dispatch({ type: 'catchUploadedPictures', value: e.target.files })}
                                />
                            </Button>
                            <Box sx={{ mt: 1 }}>
                                <Typography variant="body2">
                                    {state.uploadedPictures.length} {t('Common.PicturesSelected', 'kép kiválasztva')}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>

                    {state.error && <Alert severity="error" sx={{ mt: 3 }}>{state.error}</Alert>}

                    <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            size="large"
                            disabled={state.disabledBtn}
                            sx={{ bgcolor: 'var(--sznodarkblue)' }}
                        >
                            {state.disabledBtn ? <CircularProgress size={24} color="inherit" /> : t('Common.Submit', 'Beküldés')}
                        </Button>
                        <Button variant="outlined" size="large" onClick={() => navigate(-1)}>
                            {t('Common.Cancel', 'Mégse')}
                        </Button>
                    </Box>
                </form>
            </Paper>

            <Snackbar open={state.openSnack} autoHideDuration={3000}>
                <Alert severity="success" sx={{ width: '100%' }}>
                    {t('Common.Success', 'Sikeres mentés!')}
                </Alert>
            </Snackbar>
        </Container>
    );
}

export default AddSight;